/**
 * Demo Op Notes Library
 * 
 * Preloaded orthopedic operative note examples for demo mode.
 * These are anonymized, synthetic examples for demonstration only.
 * NO PHI - all patient identifiers are placeholders.
 */

export type DemoOpNoteType = 'shoulder' | 'knee' | 'tka' | 'meniscus' | 'ctr';

export interface DemoOpNote {
  type: DemoOpNoteType;
  title: string;
  primaryCpt: string;
  primaryCptDescription: string;
  relatedCpts: string[];
  opNoteText: string;
}

/**
 * Shoulder Arthroscopy - Rotator Cuff Repair
 */
export const DEMO_OP_NOTE_SHOULDER: DemoOpNote = {
  type: 'shoulder',
  title: 'Right Shoulder Arthroscopic Rotator Cuff Repair',
  primaryCpt: '29827',
  primaryCptDescription: 'Arthroscopy, shoulder, surgical; with rotator cuff repair',
  relatedCpts: ['29826', '29823', '29822'],
  opNoteText: `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Right shoulder rotator cuff tear, full thickness

POSTOPERATIVE DIAGNOSIS:
Same

PROCEDURE PERFORMED:
1. Right shoulder diagnostic arthroscopy
2. Arthroscopic rotator cuff repair, supraspinatus
3. Subacromial decompression
4. Extensive debridement

ANESTHESIA: General with interscalene block

PROCEDURE DETAILS:
The patient was brought to the operating room and placed in the beach chair position. 
After induction of general anesthesia and interscalene block, the right shoulder was 
prepped and draped in the usual sterile fashion.

DIAGNOSTIC ARTHROSCOPY:
Standard posterior portal was established. The glenohumeral joint was inspected. 
The articular surfaces were intact. The biceps tendon was intact. The labrum 
was without significant pathology. A full-thickness tear of the supraspinatus 
tendon was confirmed, measuring approximately 2cm in the AP dimension.

ROTATOR CUFF REPAIR:
A lateral portal was established. The bursa was debrided. The tear margins were 
freshened. Two 5.5mm PEEK suture anchors were placed at the greater tuberosity 
footprint. The supraspinatus tendon was reduced and secured using a single-row 
technique with horizontal mattress sutures. Excellent tissue purchase was obtained.

SUBACROMIAL DECOMPRESSION:
The subacromial space was visualized from the posterior portal. An anterolateral 
acromioplasty was performed using a 4.0mm burr. Adequate decompression was 
confirmed with visualization of the rotator cuff.

EXTENSIVE DEBRIDEMENT:
Extensive debridement of the subacromial bursa and degenerative tissue was 
performed throughout the procedure, exceeding 30 minutes of debridement time.

CLOSURE:
Portals were closed with 3-0 nylon. Sterile dressings were applied. The arm 
was placed in a sling.

ESTIMATED BLOOD LOSS: Minimal
SPECIMENS: None
COMPLICATIONS: None

Electronically signed by: Dr. Michael Larkin, MD
`,
};

/**
 * Knee Arthroscopy - Partial Meniscectomy
 */
export const DEMO_OP_NOTE_MENISCUS: DemoOpNote = {
  type: 'meniscus',
  title: 'Left Knee Arthroscopic Partial Meniscectomy',
  primaryCpt: '29881',
  primaryCptDescription: 'Arthroscopy, knee, surgical; with meniscectomy',
  relatedCpts: ['29880', '29877', '29875'],
  opNoteText: `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Left knee medial meniscus tear

POSTOPERATIVE DIAGNOSIS:
Left knee medial meniscus tear, complex degenerative

PROCEDURE PERFORMED:
1. Left knee diagnostic arthroscopy
2. Partial medial meniscectomy
3. Chondroplasty, medial femoral condyle

ANESTHESIA: General

PROCEDURE DETAILS:
The patient was brought to the operating room and placed supine on the operating 
table. After induction of general anesthesia, a tourniquet was placed on the 
left thigh. The left lower extremity was prepped and draped in sterile fashion.

DIAGNOSTIC ARTHROSCOPY:
The anterolateral and anteromedial portals were established. The suprapatellar 
pouch was inspected without abnormality. The patellofemoral joint showed mild 
chondromalacia of the patella. The lateral compartment showed intact meniscus 
and cartilage. The medial compartment revealed a complex degenerative tear of 
the posterior horn of the medial meniscus with an unstable flap.

PARTIAL MEDIAL MENISCECTOMY:
The unstable portion of the meniscus was resected using arthroscopic biters and 
a shaver. The remaining meniscus was contoured to a stable rim. Care was taken 
to preserve as much functional meniscus as possible.

CHONDROPLASTY:
Grade III chondromalacia was identified on the weight-bearing surface of the 
medial femoral condyle. Debridement and chondroplasty was performed to create 
a smooth, stable surface.

CLOSURE:
The joint was irrigated. Portals were closed with 3-0 nylon. Sterile dressings 
and ACE wrap were applied.

ESTIMATED BLOOD LOSS: Minimal
TOURNIQUET TIME: 28 minutes
SPECIMENS: Meniscal tissue to pathology

Electronically signed by: Dr. Michael Larkin, MD
`,
};

/**
 * Total Knee Arthroplasty
 */
export const DEMO_OP_NOTE_TKA: DemoOpNote = {
  type: 'tka',
  title: 'Right Total Knee Arthroplasty',
  primaryCpt: '27447',
  primaryCptDescription: 'Arthroplasty, knee, condyle and plateau; medial AND lateral compartments',
  relatedCpts: ['27438', '27446'],
  opNoteText: `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Right knee osteoarthritis, grade IV

POSTOPERATIVE DIAGNOSIS:
Same

PROCEDURE PERFORMED:
1. Right total knee arthroplasty
2. Patella resurfacing
3. Femoral and tibial component placement

ANESTHESIA: General

INDICATIONS:
The patient is a 65-year-old with severe right knee osteoarthritis with bone-on-bone 
changes on weight-bearing radiographs. Conservative treatment including physical therapy, 
activity modification, and intra-articular injections failed to provide adequate relief.

PROCEDURE DETAILS:
The patient was brought to the operating room and placed in the supine position. 
After induction of general anesthesia, the right lower extremity was prepped and 
draped in the usual sterile fashion. A tourniquet was placed on the right thigh.

INCISION AND EXPOSURE:
A standard midline incision was made. The medial parapatellar approach was utilized. 
The patella was everted and the knee was flexed.

TIBIAL PREPARATION:
The tibial cut was made using the extramedullary guide. The tibial plateau was 
resected perpendicular to the mechanical axis. The posterior slope was set at 3 degrees.

FEMORAL PREPARATION:
The femoral canal was entered and the intramedullary guide was placed. Distal femoral 
resection was performed at 5 degrees of valgus. Anterior and posterior femoral cuts 
were made using the sizing guide. Trial components were placed.

PATELLA RESURFACING:
The patella was prepared using the dome technique. The resection was performed 
maintaining adequate bone stock. A 32mm all-polyethylene patella component was selected.

COMPONENT IMPLANTATION:
After pulsatile lavage, cement was mixed and applied. The tibial baseplate was impacted. 
The femoral component was seated. The polyethylene insert was clicked into place. 
The patella component was cemented.

CLOSURE:
The tourniquet was released after 62 minutes. Hemostasis was achieved. The wound was 
irrigated copiously. The arthrotomy was closed with interrupted #1 Vicryl sutures. 
The subcutaneous tissue was closed with 2-0 Vicryl. The skin was closed with staples.

ESTIMATED BLOOD LOSS: 150 mL
TOURNIQUET TIME: 62 minutes
IMPLANTS USED:
- Femoral Component: Size 4, Cemented
- Tibial Baseplate: Size 3, Cemented  
- Polyethylene Insert: 10mm
- Patella: 32mm all-polyethylene

DISPOSITION:
The patient tolerated the procedure well and was transferred to the recovery room 
in stable condition.

Electronically signed by: Dr. Michael Larkin, MD
`,
};

/**
 * Knee Arthroscopy - Diagnostic
 */
export const DEMO_OP_NOTE_KNEE: DemoOpNote = {
  type: 'knee',
  title: 'Right Knee Diagnostic Arthroscopy with Synovectomy',
  primaryCpt: '29870',
  primaryCptDescription: 'Arthroscopy, knee, diagnostic, with or without synovial biopsy',
  relatedCpts: ['29875', '29876'],
  opNoteText: `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Right knee pain, suspected internal derangement

POSTOPERATIVE DIAGNOSIS:
Right knee synovitis, Grade II chondromalacia patella

PROCEDURE PERFORMED:
1. Right knee diagnostic arthroscopy
2. Partial synovectomy
3. Limited debridement

ANESTHESIA: General

PROCEDURE DETAILS:
The patient was brought to the operating room and placed supine. After induction 
of general anesthesia, the right lower extremity was prepped and draped in sterile 
fashion. A tourniquet was placed but not inflated.

DIAGNOSTIC ARTHROSCOPY:
Standard anterolateral and anteromedial portals were established. Systematic 
examination of all compartments was performed.

Suprapatellar pouch: Moderate synovitis noted. Partial synovectomy performed.
Patellofemoral joint: Grade II chondromalacia of the central patella.
Lateral compartment: Meniscus intact. Articular cartilage intact.
Medial compartment: Meniscus intact. Articular cartilage intact.
Intercondylar notch: ACL and PCL intact.

LIMITED DEBRIDEMENT:
Debridement of synovitis and loose cartilage fragments was performed. Total 
debridement time was less than 30 minutes.

CLOSURE:
The joint was irrigated with 3 liters of normal saline. Portals were closed 
with 3-0 nylon. Sterile dressings applied.

ESTIMATED BLOOD LOSS: Minimal
SPECIMENS: Synovial tissue to pathology

Electronically signed by: Dr. Michael Larkin, MD
`,
};

/**
 * Carpal Tunnel Release
 */
export const DEMO_OP_NOTE_CTR: DemoOpNote = {
  type: 'ctr',
  title: 'Left Carpal Tunnel Release',
  primaryCpt: '64721',
  primaryCptDescription: 'Neuroplasty and/or transposition; median nerve at carpal tunnel',
  relatedCpts: ['25115', '26055'],
  opNoteText: `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Left carpal tunnel syndrome

POSTOPERATIVE DIAGNOSIS:
Same

PROCEDURE PERFORMED:
1. Left open carpal tunnel release

ANESTHESIA: Local with sedation

PROCEDURE DETAILS:
The patient was brought to the operating room and placed supine with the left 
upper extremity on an arm board. After adequate sedation was achieved, the left 
hand and forearm were prepped and draped in sterile fashion.

LOCAL ANESTHESIA:
Local anesthesia was infiltrated along the planned incision line using 1% 
lidocaine with epinephrine.

INCISION:
A 3cm longitudinal incision was made in line with the ring finger ray, extending 
from the distal wrist crease to the palm. Dissection was carried through 
subcutaneous tissue.

CARPAL TUNNEL RELEASE:
The palmar fascia was divided. The transverse carpal ligament was identified 
and incised under direct visualization from distal to proximal. Complete release 
was confirmed by passing a Freer elevator proximally and distally. The median 
nerve was inspected and found to be flattened at the level of the ligament but 
otherwise healthy in appearance. No thenar branch anomalies noted.

HEMOSTASIS:
Hemostasis was obtained with bipolar cautery. The tourniquet was not used.

CLOSURE:
The skin was closed with 4-0 nylon horizontal mattress sutures. Sterile dressing 
and soft splint were applied with the wrist in neutral position.

ESTIMATED BLOOD LOSS: Minimal
SPECIMENS: None
COMPLICATIONS: None

POST-OPERATIVE PLAN:
1. Finger motion exercises immediately
2. Suture removal in 10-14 days
3. Night splint for 4 weeks

Electronically signed by: Dr. Michael Larkin, MD
`,
};

/**
 * Get all demo op notes
 */
export const ALL_DEMO_OP_NOTES: Record<DemoOpNoteType, DemoOpNote> = {
  shoulder: DEMO_OP_NOTE_SHOULDER,
  meniscus: DEMO_OP_NOTE_MENISCUS,
  tka: DEMO_OP_NOTE_TKA,
  knee: DEMO_OP_NOTE_KNEE,
  ctr: DEMO_OP_NOTE_CTR,
};

/**
 * Get demo op note by type
 */
export function getDemoOpNote(type: DemoOpNoteType): DemoOpNote {
  return ALL_DEMO_OP_NOTES[type];
}

/**
 * Get random demo op note
 */
export function getRandomDemoOpNote(): DemoOpNote {
  const types = Object.keys(ALL_DEMO_OP_NOTES) as DemoOpNoteType[];
  const randomIndex = Math.floor(Math.random() * types.length);
  return ALL_DEMO_OP_NOTES[types[randomIndex]];
}

/**
 * Get demo op note titles for selection UI
 */
export function getDemoOpNoteTitles(): Array<{ type: DemoOpNoteType; title: string; cpt: string }> {
  return Object.values(ALL_DEMO_OP_NOTES).map(note => ({
    type: note.type,
    title: note.title,
    cpt: note.primaryCpt,
  }));
}
